import { Box, Chip, Link, Stack, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

type DetailHeaderProps = {
  title: React.ReactNode
  // shows an Active/Inactive badge when the entity has an active flag
  active?: boolean
  // extra badges shown next to the title (e.g. a status)
  badges?: React.ReactNode
  // secondary line under the title, e.g. "NWA · ID 5"
  subtitle?: React.ReactNode
  // external link, e.g. the record's page in the public app
  link?: { href: string; label?: string }
  // small right-aligned lines such as sort weight and updated/created dates
  meta?: React.ReactNode[]
}
/**
 * @description Title block at the top of a detail page, above its section cards.
 */
const DetailHeader = (props: DetailHeaderProps) => {
  const { title, active, badges, subtitle, link, meta } = props
  return (
    <Stack
      direction={{ zero: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ md: 'flex-end' }}
      spacing={2}
      sx={{ mb: 3 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
          <Typography variant="h5" component="h2" sx={{ overflowWrap: 'anywhere' }}>
            {title}
          </Typography>
          {active !== undefined && (
            <Chip
              size="small"
              label={active ? 'Active' : 'Inactive'}
              color={active ? 'success' : 'error'}
              variant="outlined"
            />
          )}
          {badges}
        </Stack>
        {subtitle && (
          <Typography variant="body1" color="grey.400" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
        {link && (
          <Link
            variant="body1"
            component="a"
            href={link.href}
            target="_blank"
            sx={{ display: 'inline-block', mt: 1, overflowWrap: 'anywhere' }}
          >
            {link.label ?? link.href.replace(/^https?:\/\//, '')}
            <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'text-bottom' }} />
          </Link>
        )}
      </Box>
      {meta && meta.length > 0 && (
        <Box sx={{ flexShrink: 0, textAlign: { md: 'right' } }}>
          {meta.map((line, index) => (
            <Typography key={index} variant="body2" color="grey.500">
              {line}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  )
}
export default DetailHeader
